/**
 * Public Signal Draft Generator V1 - Draft Generators
 * 
 * Generates LinkedIn-style draft posts in different styles based on
 * sanitized closeout artifacts.
 * 
 * BOUNDARIES:
 * - Advisory only: no execution authority
 * - Deterministic output only
 * - No auto-posting capability
 * - No mutation of inputs
 */

import {
  CloseoutArtifact,
  PostStyle,
  PublicSignalAudience,
} from './types';
import { convertToPublicLanguage } from './languageConverter';

/**
 * Generate founder narrative style draft
 */
function generateFounderNarrative(
  artifact: CloseoutArtifact,
  _audience: PublicSignalAudience
): string {
  const artifactName = artifact.artifactName || 'this feature';
  const issue = artifact.issue ? convertToPublicLanguage(artifact.issue) : 'improving our system';
  const status = artifact.status || 'complete';
  
  return `Just shipped: ${artifactName}

We've been working on ${issue.toLowerCase()}. It's now ${status.toLowerCase()}.

This matters because:
• It strengthens our core infrastructure
• It improves reliability for our users
• It sets us up for future growth

Building durable systems takes time, but it's worth it.

#engineering #infrastructure #shipping`;
}

/**
 * Generate technical milestone style draft
 */
function generateTechnicalMilestone(
  artifact: CloseoutArtifact,
  _audience: PublicSignalAudience
): string {
  const artifactName = artifact.artifactName || 'this milestone';
  const module = artifact.module ? convertToPublicLanguage(artifact.module) : 'our core system';
  const testResults = artifact.testResults;
  const testSummary = testResults 
    ? `All ${testResults.total} checks passed.`
    : 'Verification complete.';

  return `Technical Milestone: ${artifactName}

We've completed a significant update to ${module.toLowerCase()}.

${testSummary}

Key improvements:
• Enhanced safety controls
• Improved verification
• Better consistency

Technical excellence isn't optional—it's foundational.

#engineering #tech #milestone`;
}

/**
 * Generate product progress style draft
 */
function generateProductProgress(
  artifact: CloseoutArtifact,
  _audience: PublicSignalAudience
): string {
  const artifactName = artifact.artifactName || 'this update';
  const issue = artifact.issue ? convertToPublicLanguage(artifact.issue) : 'improving the product';
  const changes = artifact.changes || [];
  const changeCount = changes.length;

  return `Product Update: ${artifactName}

We've made ${changeCount} improvements to address ${issue.toLowerCase()}.

What this means for you:
• More reliable performance
• Better safety guarantees
• Smoother experience

We're continuously improving based on what matters most.

#product #update #progress`;
}

/**
 * Generate customer/problem framing style draft
 */
function generateCustomerProblem(
  artifact: CloseoutArtifact,
  _audience: PublicSignalAudience
): string {
  const artifactName = artifact.artifactName || 'this solution';
  const issue = artifact.issue ? convertToPublicLanguage(artifact.issue) : 'an important challenge';

  return `Solving for ${issue.toLowerCase()}

We just completed ${artifactName.toLowerCase()} to address a real challenge our users face.

The approach:
• Focus on the root cause
• Build for reliability
• Ensure long-term sustainability

Problems worth solving require solutions worth building.

#customers #problemsolving #build`;
}

/**
 * Generate investor update style draft
 */
function generateInvestorUpdate(
  artifact: CloseoutArtifact,
  _audience: PublicSignalAudience
): string {
  const artifactName = artifact.artifactName || 'this milestone';
  const status = artifact.status || 'complete';
  const invariants = artifact.invariantsPreserved || [];
  const invariantCount = invariants.length;

  return `Development Update

Milestone: ${artifactName}
Status: ${status}

We've strengthened our core infrastructure while maintaining ${invariantCount} key safety principles.

This positions us for:
• Scalable growth
• Continued reliability
• Sustainable development

Progress is measured in foundations built, not just features shipped.

#startup #development #infrastructure`;
}

/**
 * Generate draft text based on post style
 */
export function generateDraftText(
  artifact: CloseoutArtifact,
  audience: PublicSignalAudience,
  postStyle: PostStyle
): string {
  switch (postStyle) {
    case 'founder_narrative':
      return generateFounderNarrative(artifact, audience);
    case 'technical_milestone':
      return generateTechnicalMilestone(artifact, audience);
    case 'product_progress':
      return generateProductProgress(artifact, audience);
    case 'customer_problem':
      return generateCustomerProblem(artifact, audience);
    case 'investor_update':
      return generateInvestorUpdate(artifact, audience);
    default:
      return generateProductProgress(artifact, audience);
  }
}

/**
 * Generate suggested title based on post style
 */
export function generateSuggestedTitle(
  artifact: CloseoutArtifact,
  postStyle: PostStyle
): string {
  const artifactName = artifact.artifactName || 'System Update';

  switch (postStyle) {
    case 'founder_narrative':
      return `Just Shipped: ${artifactName}`;
    case 'technical_milestone':
      return `Technical Milestone: ${artifactName}`;
    case 'product_progress':
      return `Product Update: ${artifactName}`;
    case 'customer_problem':
      return `Solving for Trust`;
    case 'investor_update':
      return `Development Update: ${artifactName}`;
    default:
      return `Update: ${artifactName}`;
  }
}
